import clsx from 'clsx';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { fmtDateWeek, fmtMonthDay, LEAVE_LABEL, nextWorkday, type LeaveType } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText, post } from '../../lib/api';
import { todayCN } from '../../lib/clock';
import { useLayout } from '../../lib/media';
import { queryClient, useLeaveActions, useLeaves, useMcpToken, useMe, useMembers, useProjects, useSettings } from '../../lib/queries';
import { setThemePref, themePref, useTheme, type ThemePref } from '../../lib/theme';
import { Icon, StatusIcon } from '../../ui/icons';
import { Avatar, BottomSheet, Button, Modal, Radio, Segmented, Toggle } from '../../ui/primitives';
import { McpPanel } from './McpPanel';
import './me.css';

// 我 · 设置与可见范围（设计稿 12e），请假面板（12c）

export function MePage() {
  const mobile = useLayout() === 'mobile';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: me } = useMe();
  const { data: members = [] } = useMembers();
  const { data: projects = [] } = useProjects();
  const { data: leaves = [] } = useLeaves();
  const { data: settings } = useSettings();
  const leaveActions = useLeaveActions();
  const [leaveOpen, setLeaveOpen] = useState(params.get('leave') === '1');
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(false);
  const { info: mcp } = useMcpToken();
  const [theme, setThemeState] = useState<ThemePref>(themePref);
  // 侧栏按钮切换后，这里的选项跟着变
  const [shown] = useTheme();
  useEffect(() => setThemeState(themePref()), [shown]);

  useEffect(() => {
    if (params.get('leave') === '1') setLeaveOpen(true);
  }, [params]);

  if (!me) return null;
  const manager = members.find((m) => m.id === me.managerId);
  const mine = projects.filter((p) => p.mine);
  const managerName = manager?.name ?? '直属主管';

  const applyTheme = (t: ThemePref) => {
    setThemeState(t);
    setThemePref(t);
  };

  const logout = async () => {
    await post('/auth/logout');
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {!mobile && <TopBar crumbs={['我']} icon="user" />}
      <div className={clsx('me', mobile && 'mobile')}>
        {mobile && <div className="me-title">我</div>}
        <div className="me-profile">
          <Avatar initial={me.initial} seed={me.name} size={44} />
          <div className="col" style={{ gap: 2, minWidth: 0 }}>
            <span className="name">{me.name}</span>
            <span className="sub">
              {[me.teamName ?? me.title, manager ? `汇报给${manager.name}` : null].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>

        <Section title="账号">
          <Line k="工作邮箱" v={me.email ?? '未登记'} />
          <Line
            k="微信"
            v={me.wechatBound ? '已绑定，可以扫码登录' : '未绑定 · 下次用微信扫码登录时绑定'}
            right={
              me.wechatBound ? (
                <Button
                  variant="text"
                  mobile={mobile}
                  onClick={async () => {
                    await post('/me/unbind-wechat');
                    await queryClient.invalidateQueries({ queryKey: ['me'] });
                  }}
                >
                  解绑
                </Button>
              ) : undefined
            }
          />
        </Section>

        <Section title="日报">
          <Line k="提醒时间" v={`截止 ${settings?.deadline ?? '19:00'} · 团队统一设置`} right={settings?.prefillReminder ?? '17:30'} />
          <Line k="负责的项目" v={mine.map((p) => p.name).join('、') || '还没有'} right={`${mine.length} 个`} onClick={() => setProjectsOpen(true)} />
          <Line
            k="请假与休息"
            v={leaves.length ? leaves.map((l) => `${LEAVE_LABEL[l.type]} ${fmtMonthDay(l.from)}${l.to !== l.from ? `–${fmtMonthDay(l.to)}` : ''}`).join('、') : '无'}
            right={<Icon name="chevR" size={14} />}
            onClick={() => setLeaveOpen(true)}
          />
        </Section>

        {leaves.length > 0 && (
          <div className="me-leaves">
            {leaves.map((l) => (
              <div key={l.id} className="me-leave">
                <Icon name={l.type === 'trip' ? 'plane' : 'sun'} size={16} />
                <span className="flex1">
                  {LEAVE_LABEL[l.type]} · {fmtDateWeek(l.from)}
                  {l.to !== l.from ? ` – ${fmtDateWeek(l.to)}` : ''}
                </span>
                <Button variant="text" mobile={mobile} onClick={() => leaveActions.cancel.mutate(l.id)}>
                  撤销
                </Button>
              </div>
            ))}
          </div>
        )}

        <Section title="谁能看到什么">
          <div className="me-vis">
            <div className="k">{managerName}能看到</div>
            <div className="v">你提交后的日报、AI 追问和你的回复、你标记的请假</div>
          </div>
          <div className="me-vis">
            <div className="k">{managerName}看不到</div>
            <div className="v">草稿、提交前的修改记录、你点过的“跳过追问”</div>
          </div>
          <button type="button" className="me-line" onClick={() => setAiOpen(!aiOpen)}>
            <span className="k">AI 如何使用你的日报</span>
            <span className="flex1" />
            <Icon name={aiOpen ? 'chevU' : 'chevD'} size={14} />
          </button>
          {aiOpen && (
            <div className="me-note">
              AI 只读日报里的文字，用来预填、追问含糊的地方、发现项目卡点。发给 AI 之前，人名和客户名会换成代号；AI 只谈项目，不评价个人表现，所有判断都由人来做决定。
            </div>
          )}
        </Section>

        <Section title="更多">
          <Line
            k="通知"
            v={me.unreadNotifications ? `${me.unreadNotifications} 条未读` : '日报提醒、追问、指派等，邮件同步发送'}
            right={<Icon name="chevR" size={14} />}
            onClick={() => navigate('/notifications')}
          />
          <Line k="问 AI" v="用日报回答项目进展、卡点、谁在做什么" right={<Icon name="chevR" size={14} />} onClick={() => navigate('/ask')} />
          {(me.programCount > 0 || me.isManager) && (
            <Line k="项目集" v="关键任务对照计划，每期出进度汇报" right={<Icon name="chevR" size={14} />} onClick={() => navigate('/programs')} />
          )}
          {me?.isManager && <Line k="看板" v="日报提交、项目状态、待处理的异常" right={<Icon name="chevR" size={14} />} onClick={() => navigate('/dashboard')} />}
          {me?.isManager && <Line k="团队状态" v="本周建议关注：值得认可、建议沟通（仅你可见）" right={<Icon name="chevR" size={14} />} onClick={() => navigate('/team')} />}
          {me?.isManager && <Line k="周报" v="AI 根据本周日报起草，核对后发送给管理层" right={<Icon name="chevR" size={14} />} onClick={() => navigate('/weekly')} />}
          <Line
            k="设置"
            v={me?.role === 'admin' ? '日报规则、成员与团队、数据与日志' : '日报规则、可见范围（只读）'}
            right={<Icon name="chevR" size={14} />}
            onClick={() => navigate('/settings')}
          />
        </Section>

        <Section title="本地智能体">
          <Line
            k="MCP 令牌"
            v={mcp.data?.exists ? `已开启 · 末 4 位 ${mcp.data.hint}` : '让 Claude Code、Codex、手机 Claude 等智能体代你写日报、查项目'}
            right={<Icon name="chevR" size={14} />}
            onClick={() => setMcpOpen(true)}
          />
        </Section>

        <Section title="外观">
          <div className="me-line static">
            <span className="k">主题</span>
            <span className="flex1" />
            <Segmented
              items={[
                { value: 'dark', label: '深色' },
                { value: 'light', label: '浅色' },
                { value: 'system', label: '跟随系统' },
              ]}
              value={theme}
              onChange={applyTheme}
            />
          </div>
        </Section>

        <div className="me-foot">
          <Button mobile={mobile} variant="ghost" onClick={() => void logout()}>
            退出登录
          </Button>
          <p className="t-meta">通知规则：{settings?.prefillReminder ?? '17:30'} 预填提醒 → 未提交 {settings?.lateReminder ?? '21:00'} 再提醒一次 → 休息日 / 请假日不提醒；主管追问即时推送。</p>
        </div>
      </div>

      {leaveOpen && (
        <LeavePanel
          mobile={mobile}
          onClose={() => {
            setLeaveOpen(false);
            if (params.get('leave')) setParams({}, { replace: true });
          }}
        />
      )}
      {projectsOpen && <ProjectsPanel mobile={mobile} onClose={() => setProjectsOpen(false)} />}
      {mcpOpen && <McpPanel mobile={mobile} onClose={() => setMcpOpen(false)} />}
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="me-sec">
      <div className="me-sec-title">{title}</div>
      {children}
    </section>
  );
}

function Line({ k, v, right, onClick }: { k: string; v: string; right?: ReactNode; onClick?: () => void }) {
  const inner = (
    <>
      <span className="k">{k}</span>
      <span className="v ellipsis">{v}</span>
      {right && <span className="r">{right}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className="me-line" onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className="me-line static">{inner}</div>
  );
}

// ---------------------------------------------------------------------------
// 请假 / 出差 / 休息（设计稿 12c）
// ---------------------------------------------------------------------------

export function LeavePanel({ mobile, onClose }: { mobile: boolean; onClose: () => void }) {
  const { create } = useLeaveActions();
  const today = todayCN();
  const [type, setType] = useState<LeaveType>('leave');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const options: { t: LeaveType; title: string; sub: string }[] = [
    { t: 'leave', title: '请假', sub: '不需要写日报' },
    { t: 'trip', title: '出差', sub: '写一句话即可，不追问' },
    { t: 'rest', title: '调休 / 休息', sub: '不需要写日报' },
  ];
  const confirm = async () => {
    try {
      await create.mutateAsync({ type, from, to: to < from ? from : to, note: note.trim() || null, notifyOwners: notify });
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const back = nextWorkday(to < from ? from : to, { holidays: [], extraWorkdays: [] });

  const body = (
    <div className="me-leave-panel">
      <div className="t-block-title">今天不上班？</div>
      <div className="t-meta" style={{ marginTop: 4 }}>
        期间不提醒、不预填，你的项目不会被算作停滞。
      </div>
      <div style={{ margin: '12px -20px 0' }}>
        {options.map((o) => (
          <button key={o.t} type="button" className={clsx('ib-opt', type === o.t && 'on')} onClick={() => setType(o.t)} style={{ display: 'flex', gap: 12, padding: '10px 20px', width: '100%', textAlign: 'left' }}>
            <Radio checked={type === o.t} large={mobile} />
            <span className="col" style={{ gap: 2 }}>
              <span className="c1" style={{ fontSize: mobile ? 15 : 13 }}>
                {o.title}
              </span>
              <span className="t-meta">{o.sub}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <input type="date" className={clsx('wd-input', mobile && 'mobile')} style={{ width: 'auto' }} value={from} min={today} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label="开始日期" />
        <span className="c4">–</span>
        <input type="date" className={clsx('wd-input', mobile && 'mobile')} style={{ width: 'auto' }} value={to < from ? from : to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} aria-label="结束日期" />
      </div>
      {type === 'trip' && (
        <input className={clsx('wd-input', mobile && 'mobile')} style={{ marginTop: 10 }} placeholder="一句话说明，例如：去慕尼黑见渠道商" value={note} onChange={(e) => setNote(e.target.value)} />
      )}
      <div className="row" style={{ gap: 10, marginTop: 14, fontSize: mobile ? 15 : 13 }}>
        <Toggle on={notify} onChange={setNotify} label="告知项目负责人" />
        <span className="c2">告知项目负责人</span>
      </div>
      <div className="t-meta" style={{ marginTop: 10 }}>
        {fmtDateWeek(back)} 回来后，日报会按你最近写的明日计划预填。
      </div>
      {error && <div className="t-meta c-red" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );

  if (mobile) {
    return (
      <BottomSheet onClose={onClose}>
        <div style={{ padding: '4px 20px 0' }}>{body}</div>
        <div className="row" style={{ gap: 8, padding: '16px 20px 0' }}>
          <Button mobile full onClick={onClose}>
            取消
          </Button>
          <Button mobile full variant="primary" disabled={create.isPending} onClick={() => void confirm()}>
            确认
          </Button>
        </div>
      </BottomSheet>
    );
  }
  return (
    <Modal
      width={480}
      title="请假 / 出差"
      onClose={onClose}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={create.isPending} onClick={() => void confirm()}>
            确认
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 负责的项目：加入新项目；退出需负责人确认
// ---------------------------------------------------------------------------

function ProjectsPanel({ mobile, onClose }: { mobile: boolean; onClose: () => void }) {
  const { data: projects = [] } = useProjects();
  const [error, setError] = useState<string | null>(null);
  const active = projects.filter((p) => p.status !== 'done' && p.status !== 'cancelled');
  const join = async (id: string) => {
    try {
      await post('/me/onboarding', { projectIds: [id] });
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(errorText(e));
    }
  };
  const exit = async (id: string) => {
    try {
      await post(`/projects/${id}/exit`, { reason: '这个项目不归我了', handoverToId: null });
      setError('已提交退出申请，等项目负责人确认');
    } catch (e) {
      setError(errorText(e));
    }
  };
  const list = (
    <div>
      {active.map((p) => (
        <div key={p.id} className="me-proj">
          <StatusIcon status={p.status} />
          <span className="col flex1" style={{ gap: 1 }}>
            <span className="c1">
              {p.name} <span className="t-meta">{p.code}</span>
            </span>
            <span className="t-meta">{p.ownerName}负责</span>
          </span>
          {p.mine ? (
            <Button variant="text" mobile={mobile} onClick={() => void exit(p.id)}>
              不归我了
            </Button>
          ) : (
            <Button variant="ghost" mobile={mobile} onClick={() => void join(p.id)}>
              加入
            </Button>
          )}
        </div>
      ))}
      {error && <div className="t-meta" style={{ padding: '10px 0' }}>{error}</div>}
    </div>
  );
  return mobile ? (
    <BottomSheet onClose={onClose}>
      <div style={{ padding: '4px 20px 12px' }}>
        <div className="t-block-title" style={{ marginBottom: 8 }}>
          负责的项目
        </div>
        {list}
      </div>
    </BottomSheet>
  ) : (
    <Modal width={520} title="负责的项目" subtitle="加入后每天的日报会按这个项目预填" onClose={onClose}>
      {list}
    </Modal>
  );
}

